import { useState, type FormEvent } from 'react';
import {
  useAccountVersions, useClientAccounts, useEditAccount, useEndAccount, useRecordAccount,
  type AccountKind, type AccountValues, type AccountVersion, type ClientAccount,
} from '../api/clientAccounts.ts';
import { useEditReasons, type EditReason } from '../api/contacts.ts';
import { ApiError } from '../api/client.ts';
import type { EditLike } from '../earlierVersions.ts';
import { Panel } from './Panel.tsx';
import { LoadFailure } from './LoadFailure.tsx';
import { RecordEdit } from './RecordEdit.ts';
import { EarlierVersions, EditedMarker } from './EarlierVersions.tsx';
import { ReasonFields, reasonReady } from './contacts/ReasonFields.tsx';
import { field, hint, label } from './contacts/Sheet.tsx';

/**
 * "Client accounts" (P-56; Arthur, A-#506, A-#512): the online accounts Allied made for the client and the phones it
 * provided, on the Dashboard, CLOSED by default. Nothing is asked of the server until it is opened, and the values
 * leave the query cache when it closes: the read lives only in OpenAccounts, rendered only while open, so closing
 * unmounts it and gcTime 0 drops them (api/clientAccounts.ts; Code 1's read, F1). Read by everyone who sees the client; Edit (change,
 * end) and Add only for a reader the server says may write (`may_write`, client_master at write). Plain text, as
 * ruled: each account a card with its fields as typed. A refusal is shown as the server sent it, which names the
 * field and never a value. Nothing here logs anything.
 */

type Field = keyof AccountValues;
const FIELDS: Record<AccountKind, [Field, string][]> = {
  email: [['label', 'Provider'], ['login', 'Email address'], ['password', 'Password']],
  login: [['label', 'What it is for'], ['login', 'Login'], ['password', 'Password']],
  phone: [['phone_number', 'Phone number'], ['carrier', 'Carrier'], ['login', 'Account login'], ['password', 'Account password'], ['pin', 'PIN']],
};
const KIND_WORDS: Record<AccountKind, string> = { email: 'Email account', login: 'Login', phone: 'Phone' };
const MAX: Record<Field, number> = { label: 120, login: 254, password: 200, phone_number: 32, carrier: 80, pin: 12 };

const button =
  'inline-flex min-h-[44px] items-center rounded-lg border border-neutral-400 px-4 text-[0.9375rem] font-semibold text-neutral-800 hover:bg-tint';
const primary =
  'inline-flex min-h-[44px] items-center rounded-lg bg-brand px-4 text-[0.9375rem] font-semibold text-on-brand disabled:bg-neutral-300 disabled:text-neutral-700';

/** An instant in the viewer's own zone (a timestamptz: converting it is right). */
const day = (instant: string) => new Date(instant).toLocaleDateString(undefined, { dateStyle: 'medium' });

export function ClientAccountsPanel({ clientId }: { clientId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Panel title="Client accounts" span={12}
           action={
             <button type="button" aria-expanded={open} className={button} onClick={() => setOpen((o) => !o)}>
               {open ? 'Hide the accounts' : 'Show the accounts'}
             </button>
           }>
      {open ? <OpenAccounts clientId={clientId} /> : (
        <p className="px-3 py-3 text-sm text-neutral-600">
          The online accounts and phones Allied set up for this client. Closed until you open it.
        </p>
      )}
    </Panel>
  );
}

/**
 * The open panel. The ONLY caller of useClientAccounts: unmounted on close, which leaves the query no observer, so
 * gcTime 0 drops the values at once. An unsaved Add ends with it.
 */
function OpenAccounts({ clientId }: { clientId: string }) {
  const [adding, setAdding] = useState(false);
  const q = useClientAccounts(clientId);
  const d = q.data;
  if (q.error && !d) {
    return <LoadFailure what="Client accounts" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching}
                        forbiddenIsSession={false} />;
  }
  if (!d) return <p className="px-3 py-3 text-sm text-neutral-600">Loading…</p>;
  return (
    <div className="grid gap-3 px-3 py-3">
      {d.may_write && !adding && (
        <p><button type="button" className={button} onClick={() => setAdding(true)}>Add an account</button></p>
      )}
      {adding && <AddAccount clientId={clientId} onDone={() => setAdding(false)} />}
      {d.accounts.length === 0 && !adding && <p className="text-sm text-neutral-600">No accounts are recorded.</p>}
      <ul className="grid gap-3 md:grid-cols-2">
        {d.accounts.map((a) => <AccountCard key={a.id} clientId={clientId} a={a} mayWrite={d.may_write} />)}
      </ul>
    </div>
  );
}

function AccountCard({ clientId, a, mayWrite }: { clientId: string; a: ClientAccount; mayWrite: boolean }) {
  const [doing, setDoing] = useState<'change' | 'end' | null>(null);
  const versions = useAccountVersions(clientId, a.id, a.version > 1);
  const edits = versions.data ? editsOf(versions.data.versions) : undefined;
  const first = versions.data?.versions[0];
  return (
    <li className="rounded-lg border border-line bg-panel px-3 py-2">
      <h3 className="font-semibold text-neutral-900">
        {KIND_WORDS[a.kind]}{a.label && a.kind !== 'phone' ? `: ${a.label}` : ''}
      </h3>
      {a.ended_at && (
        <p className="text-sm font-semibold text-neutral-700">Ended {day(a.ended_at)}: {a.ended_reason}</p>
      )}
      <dl className="mt-1 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[0.9375rem]">
        {FIELDS[a.kind].filter(([f]) => a[f] !== null).map(([f, words]) => (
          <div key={f} className="contents">
            <dt className="text-neutral-600">{words}</dt>
            <dd className="font-mono break-all whitespace-pre-wrap text-neutral-900">{a[f]}</dd>
          </div>
        ))}
      </dl>
      {a.version > 1 && <EditedMarker edits={edits} />}
      {a.version > 1 && (
        <EarlierVersions edits={edits} first={first ? { by: first.recorded_by, at: first.recorded_at } : null}
                         loading={versions.isPending} error={versions.error} />
      )}
      {doing === null && (
        <RecordEdit may={mayWrite && a.ended_at === null} choices={[
          { key: 'change', label: 'Change', onPick: () => setDoing('change') },
          { key: 'end', label: 'End this account', onPick: () => setDoing('end') },
        ]} />
      )}
      {doing === 'change' && <ChangeAccount clientId={clientId} a={a} onDone={() => setDoing(null)} />}
      {doing === 'end' && <EndAccount clientId={clientId} a={a} onDone={() => setDoing(null)} />}
    </li>
  );
}

/** Each version after the first as the edit that made it: what it changed, before and after (earlierVersions.ts). */
function editsOf(list: AccountVersion[]): EditLike[] {
  const words: Record<Field, string> = { label: 'Provider or label', login: 'Login', password: 'Password',
    phone_number: 'Phone number', carrier: 'Carrier', pin: 'PIN' };
  return list.slice(1).map((v, i) => {
    const prev = list[i]!;
    return {
      version: v.version, kind: 'edit', by: v.recorded_by, at: v.recorded_at, reason: v.edit_reason_label, reason_text: v.reason_text,
      changes: (Object.keys(words) as Field[]).filter((f) => prev[f] !== v[f])
        .map((f) => ({ field: f, label: words[f], before: prev[f], after: v[f] })),
    };
  });
}

function Refusal({ error }: { error: Error | null }) {
  if (!error) return null;
  const e = error instanceof ApiError ? error : null;
  return (
    <p role="alert" className="rounded-lg border-l-8 border-critical-edge bg-panel px-3 py-2 text-sm text-critical-edge">
      {e ? e.message : 'It could not be saved. Try again.'}
      {e?.hint && <span className="block text-neutral-700">{e.hint}</span>}
    </p>
  );
}

function ValueFields({ id, kind, values, onChange }: {
  id: string; kind: AccountKind; values: Partial<AccountValues>; onChange: (f: Field, v: string) => void;
}) {
  return (
    <>
      {FIELDS[kind].map(([f, words]) => (
        <div key={f}>
          <label htmlFor={`${id}-${f}`} className={label}>{words}</label>
          <input id={`${id}-${f}`} className={field} value={values[f] ?? ''} maxLength={MAX[f]}
                 autoComplete="off" spellCheck={false}
                 inputMode={f === 'pin' || f === 'phone_number' ? 'tel' : undefined}
                 onChange={(e) => onChange(f, e.target.value)} />
        </div>
      ))}
    </>
  );
}

/** Blank fields are sent as null: the server keeps nothing for them. */
const sendable = (kind: AccountKind, values: Partial<AccountValues>) =>
  Object.fromEntries(FIELDS[kind].map(([f]) => [f, values[f]?.length ? values[f] : null])) as Partial<AccountValues>;

function AddAccount({ clientId, onDone }: { clientId: string; onDone: () => void }) {
  const [kind, setKind] = useState<AccountKind>('email');
  const [values, setValues] = useState<Partial<AccountValues>>({});
  const record = useRecordAccount(clientId);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    record.mutate({ kind, values: sendable(kind, values) }, { onSuccess: () => { record.reset(); onDone(); } });
  };
  return (
    <form onSubmit={submit} className="grid gap-2 rounded-lg border border-line px-3 py-2">
      <fieldset>
        <legend className={label}>What kind</legend>
        <div className="mt-1 flex flex-wrap gap-2">
          {(Object.keys(KIND_WORDS) as AccountKind[]).map((k) => (
            <label key={k} className={`inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm
                     ${kind === k ? 'border-brand bg-tint font-semibold text-neutral-900' : 'border-neutral-300 text-neutral-800'}`}>
              <input type="radio" name="account-kind" value={k} checked={kind === k} onChange={() => setKind(k)} className="h-4 w-4" />
              {KIND_WORDS[k]}
            </label>
          ))}
        </div>
      </fieldset>
      <ValueFields id="add" kind={kind} values={values} onChange={(f, v) => setValues((s) => ({ ...s, [f]: v }))} />
      <Refusal error={record.error} />
      <p className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={record.isPending}>{record.isPending ? 'Saving…' : 'Save the account'}</button>
        <button type="button" className={button} onClick={() => { record.reset(); onDone(); }}>Cancel</button>
      </p>
    </form>
  );
}

function ChangeAccount({ clientId, a, onDone }: { clientId: string; a: ClientAccount; onDone: () => void }) {
  const [values, setValues] = useState<Partial<AccountValues>>(
    Object.fromEntries(FIELDS[a.kind].map(([f]) => [f, a[f] ?? ''])) as Partial<AccountValues>);
  const [reason, setReason] = useState<EditReason | null>(null);
  const [text, setText] = useState('');
  const reasons = useEditReasons();
  const edit = useEditAccount(clientId);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!reason) return;
    edit.mutate({ id: a.id, values: sendable(a.kind, values), reason, reason_text: text.trim() || undefined },
      { onSuccess: () => { edit.reset(); onDone(); } });
  };
  return (
    <form onSubmit={submit} className="mt-2 grid gap-2">
      <ValueFields id={`change-${a.id}`} kind={a.kind} values={values} onChange={(f, v) => setValues((s) => ({ ...s, [f]: v }))} />
      <ReasonFields id={`change-${a.id}`} reason={reason} text={text} onReason={setReason} onText={setText} />
      <Refusal error={edit.error} />
      <p className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={edit.isPending || !reasonReady(reasons, reason, text)}>
          {edit.isPending ? 'Saving…' : 'Save the change'}
        </button>
        <button type="button" className={button} onClick={() => { edit.reset(); onDone(); }}>Cancel</button>
      </p>
    </form>
  );
}

function EndAccount({ clientId, a, onDone }: { clientId: string; a: ClientAccount; onDone: () => void }) {
  const [why, setWhy] = useState('');
  const end = useEndAccount(clientId);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    end.mutate({ id: a.id, reason: why.trim() }, { onSuccess: () => { end.reset(); onDone(); } });
  };
  return (
    <form onSubmit={submit} className="mt-2 grid gap-2">
      <label htmlFor={`end-${a.id}`} className={label}>Why it is ending</label>
      <input id={`end-${a.id}`} className={field} value={why} maxLength={2000} onChange={(e) => setWhy(e.target.value)} />
      <p className={hint}>The account stays on record as it was, marked ended. Write why, never a password or PIN.</p>
      <Refusal error={end.error} />
      <p className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={end.isPending || !why.trim()}>
          {end.isPending ? 'Ending…' : 'End the account'}
        </button>
        <button type="button" className={button} onClick={() => { end.reset(); onDone(); }}>Cancel</button>
      </p>
    </form>
  );
}
